import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Collapse } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalText from '../common/LocalText';
import { fmtLocalDate, formatDateRanges } from '../../utils/dateUtils';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ["2026-10-01", ...] -> [{ key, label, year, month, days:Set<number> }] for every month that has a date.
const groupByMonth = (dates) => {
  const months = new Map();
  dates.forEach((d) => {
    const key = d.slice(0, 7);
    if (!months.has(key)) {
      const year = Number(d.slice(0, 4));
      const month = Number(d.slice(5, 7)) - 1;
      months.set(key, { key, year, month, label: `${MONTH_NAMES[month]} ${year}`, days: new Set() });
    }
    months.get(key).days.add(Number(d.slice(8, 10)));
  });
  return [...months.values()];
};

const MiniMonth = ({ year, month, label, days }) => {
  const blanks = new Date(year, month, 1).getDay();
  const length = new Date(year, month + 1, 0).getDate();
  return (
    <Box sx={{ minWidth: 168 }} data-testid={`run-history-month-${label.replace(' ', '-')}`}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#5383b3', mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 22px)', gap: '2px' }}>
        {WEEKDAYS.map((w, i) => (
          <Typography key={`w${i}`} sx={{ fontSize: 9, textAlign: 'center', color: '#94a3b8' }}>{w}</Typography>
        ))}
        {Array.from({ length: blanks }, (_, i) => <Box key={`b${i}`} />)}
        {Array.from({ length }, (_, i) => {
          const day = i + 1;
          const on = days.has(day);
          return (
            <Box
              key={day}
              sx={{
                height: 22, borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontVariantNumeric: 'tabular-nums',
                bgcolor: on ? '#c7d8ec' : 'transparent', color: on ? '#0a326d' : '#b0bec5', fontWeight: on ? 700 : 400,
              }}
            >
              {day}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Read-only record of the days an ad was scheduled BEFORE today and OUTSIDE its campaign's current dates
 * (for example October, after the campaign was moved to November). These rows are kept by the API, never counted in
 * "N days selected", and can't be edited. `dates` are "yyyy-MM-dd".
 */
const AdRunHistory = ({ dates, campaignStart, campaignEnd }) => {
  const [open, setOpen] = useState(false);
  const months = useMemo(() => groupByMonth(dates ?? []), [dates]);

  if (!dates || dates.length === 0) return null;

  const windowText = campaignStart && campaignEnd ? `${fmtLocalDate(campaignStart)} – ${fmtLocalDate(campaignEnd)}` : null;

  return (
    <Box
      data-testid="ad-run-history"
      sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', bgcolor: '#f7faff', border: '1px solid #dde6f0' }}
    >
      <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
        <HistoryIcon sx={{ fontSize: 15, color: '#5383b3' }} />
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10 }}>
          <LocalText>Earlier run dates</LocalText>
        </Typography>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 10 }}>
          <LocalText>read-only</LocalText>
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>
        {formatDateRanges(dates, 4)}
        <Box component="span" sx={{ fontWeight: 500, color: '#64748b' }}>
          {` · ${dates.length} day${dates.length === 1 ? '' : 's'}`}
        </Box>
      </Typography>

      <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
        <LocalText>
          {windowText
            ? `These days were scheduled outside this campaign's current dates (${windowText}) and are already past. They stay on record and can't be changed.`
            : 'These days are already past and outside this campaign\'s current dates. They stay on record and can\'t be changed.'}
        </LocalText>
      </Typography>

      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        startIcon={<CalendarMonthIcon sx={{ fontSize: 15 }} />}
        sx={{ mt: 0.5, px: 0.5, textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#5383b3', '&:hover': { bgcolor: '#edf2fb' } }}
      >
        {open ? 'Hide calendar' : 'Show calendar'}
      </Button>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, maxHeight: 260, overflowY: 'auto' }}>
          {months.map(({ key, ...month }) => <MiniMonth key={key} {...month} />)}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AdRunHistory;
